import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fetchClientCommunications, fetchPublications, fetchClientIntegrations, fetchAccountGrowth, fetchCompliance, fetchCommsPlans, fetchSheetSyncStatus, triggerSheetSync, fetchGrowthCorporate, fetchGrowthPersonal, CONTENT_TYPE_META, formatNumber } from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ComposedChart, Area,
} from "recharts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Megaphone, ExternalLink, Heart, MessageCircle, Share2, Eye, Search, Filter, RefreshCw, CheckCircle2, AlertTriangle, Repeat2, TrendingUp, Target, BarChart3, ListChecks, Briefcase, User, Calendar, FileText, Info, Layers, Table as TableIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import CommsSettingsDialog from "@/pages/client-tabs/CommsSettingsDialog";
import OtrosSection from "@/pages/client-tabs/OtrosSection";
import MonthlyMetricsTable from "@/pages/client-tabs/MonthlyMetricsTable";

const TRACKED_TYPES = ["Corpo", "Personal", "News L", "Blog"];
const ALL_TYPES = ["Corpo", "Personal", "News L", "Blog", "Instagram"];

const STATUS_TONES = {
  on_track: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", bar: "bg-emerald-500", label: "On track", short: "OK" },
  in_progress: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", bar: "bg-sky-500", label: "En proceso", short: "··" },
};

const SUB_TABS = [
  { value: "tablero", label: "Tablero mensual", icon: TableIcon, desc: "Todas las métricas mes a mes con evolución" },
  { value: "crecimiento", label: "Crecimiento", icon: TrendingUp, desc: "Evolución mes a mes de perfil corporativo y personal" },
  { value: "analisis", label: "Análisis Metricool", icon: BarChart3, desc: "Resultados detallados de publicaciones desde Metricool" },
  { value: "backlog", label: "Backlog", icon: ListChecks, desc: "Listado completo del backlog interno" },
  { value: "otros", label: "Otros", icon: Layers, desc: "YouTube y otras plataformas adicionales" },
  { value: "compromiso", label: "Compromiso", icon: Target, desc: "Cumplimiento total del plan + alcance de objetivos" },
];

export default function CommunicationsTab({ clientId }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["client-communications", clientId, 6],
    queryFn: () => fetchClientCommunications(clientId, 6),
  });
  const { data: integrations = [] } = useQuery({
    queryKey: ["client-integrations", clientId],
    queryFn: () => fetchClientIntegrations(clientId),
  });
  const { data: mappings = [] } = useQuery({
    queryKey: ["client-metricool-mappings", clientId],
    queryFn: () => api.get(`/clients/${clientId}/metricool/mappings`).then((r) => r.data),
  });

  const metricoolIntegration = integrations.find((i) => i.platform === "metricool");
  const metricoolConnected = !!metricoolIntegration?.connected && mappings.length > 0;

  const syncMutation = useMutation({
    mutationFn: () => api.post(`/clients/${clientId}/metricool/sync`).then((r) => r.data),
    onSuccess: (res) => {
      toast.success(`Sincronizado · ${res.imported} publicaciones`);
      qc.invalidateQueries({ queryKey: ["client-communications", clientId] });
      qc.invalidateQueries({ queryKey: ["client-publications", clientId] });
      qc.invalidateQueries({ queryKey: ["client-growth", clientId] });
    },
    onError: (e) => toast.error("Error al sincronizar: " + (e.response?.data?.detail || e.message)),
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-36 rounded-md skeleton" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="communications-tab">
      {/* (Origen de datos del módulo removido — la info ya queda implícita en cada bloque) */}

      {/* Sub-tabs */}
      <Tabs defaultValue="tablero" className="w-full" data-testid="communications-subtabs">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <TabsList className="h-auto p-1 bg-slate-100 border border-slate-200 rounded-md flex flex-wrap gap-1">
            {SUB_TABS.map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger
                  key={t.value} value={t.value} data-testid={`subtab-${t.value}`}
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3.5 py-1.5 rounded-sm text-sm font-medium gap-2"
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
          <CommsSettingsDialog clientId={clientId} />
        </div>

        <TabsContent value="compromiso" className="mt-6">
          <CompromisoSection data={data} />
        </TabsContent>
        <TabsContent value="tablero" className="mt-6">
          <MonthlyMetricsTable clientId={clientId} />
        </TabsContent>
        <TabsContent value="crecimiento" className="mt-6">
          <CrecimientoSection clientId={clientId} metricoolConnected={metricoolConnected} />
        </TabsContent>
        <TabsContent value="analisis" className="mt-6">
          <AnalisisSection clientId={clientId} metricoolConnected={metricoolConnected} />
        </TabsContent>
        <TabsContent value="backlog" className="mt-6">
          <BacklogSection clientId={clientId} totalCount={data.totals.publications} reconciliation={data.reconciliation} />
        </TabsContent>
        <TabsContent value="otros" className="mt-6">
          <OtrosSection clientId={clientId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ========== Section 1: COMPROMISO ========== */
function CompromisoSection({ data }) {
  const { monthly_chart, monthly_commitments, tracked_types } = data;
  const tracked = tracked_types && tracked_types.length ? tracked_types : TRACKED_TYPES;
  return (
    <div className="space-y-8">
      <PlanCompliance clientId={data.client_id} />

      <section className="bg-white border border-slate-200 rounded-md p-5">
        <SectionHeader title="Prometido vs. ejecutado por mes" subtitle="Barras = entregado · línea punteada = target mensual" small />
        <MonthlyDeliveryChart data={monthly_chart} commitments={monthly_commitments} trackedTypes={tracked} />
      </section>
    </div>
  );
}

function PlanMetricsBlock({ plan }) {
  const et = plan?.engagement_targets || {};
  const metrics = [
    { key: "linkedin_impressions", label: "Impresiones LinkedIn", icon: Eye, color: "#2563EB" },
    { key: "interactions", label: "Interacciones", icon: Heart, color: "#EC4899" },
    { key: "clicks", label: "Clicks a web/perfil", icon: ExternalLink, color: "#0EA5E9" },
    { key: "followers_growth", label: "Nuevos seguidores", icon: TrendingUp, color: "#10B981" },
    { key: "newsletter_subs", label: "Suscriptores News LinkedIn", icon: FileText, color: "#7C3AED" },
  ];

  const fmt = (n) => {
    if (!n) return "—";
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
    return n.toLocaleString();
  };

  const visible = metrics.filter((m) => (et[`${m.key}_min`] || 0) > 0 || (et[`${m.key}_max`] || 0) > 0);
  if (visible.length === 0) return null;

  return (
    <div className="mt-6 pt-5 border-t border-slate-100" data-testid="plan-metrics-block">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Target className="h-3.5 w-3.5 text-slate-500" />
        <h4 className="font-display text-sm font-semibold text-slate-900">Métricas mensuales a monitorear</h4>
        <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">
          Rangos esperados · pendiente conectar Metricool
        </span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {visible.map((m) => {
          const mn = et[`${m.key}_min`] || 0;
          const mx = et[`${m.key}_max`] || 0;
          const Icon = m.icon;
          return (
            <div key={m.key} className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid={`plan-metric-${m.key}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="h-6 w-6 rounded-md flex items-center justify-center shrink-0" style={{ background: `${m.color}1A`, color: m.color }}>
                  <Icon className="h-3 w-3" />
                </span>
                <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold truncate">{m.label}</span>
              </div>
              <div className="font-mono text-base font-semibold text-slate-900 tabular-nums">
                {fmt(mn)} <span className="text-slate-400 mx-0.5">–</span> {fmt(mx)}
              </div>
              <div className="text-[10px] text-slate-400 font-mono mt-0.5">por mes</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlanCompliance({ clientId }) {
  const [viewMode, setViewMode] = useState("plan"); // "plan" | "period"
  const [complianceView, setComplianceView] = useState("volumenes"); // "volumenes" | "resumen"
  const [planId, setPlanId] = useState(null);
  const [granularity, setGranularity] = useState("yearly"); // "monthly" | "bimonthly" | "quarterly" | "yearly"
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [period, setPeriod] = useState(Math.floor(now.getMonth() / 3) + 1);

  // Load available plans
  const { data: plans = [] } = useQuery({
    queryKey: ["comms-plans", clientId],
    queryFn: () => fetchCommsPlans(clientId),
  });

  // Auto-select most-recent plan when plans load and none chosen yet
  if (viewMode === "plan" && !planId && plans.length > 0) {
    // pick the plan that contains "today", else the latest one
    const today = new Date().toISOString().slice(0, 10);
    const containing = plans.find((p) => p.start_date <= today && (!p.end_date || p.end_date >= today));
    const picked = containing || plans[plans.length - 1];
    // setState during render is fine in this guarded one-shot pattern
    setPlanId(picked.id);
  }

  // Clients without any plan → auto-switch to "Año completo" so the tabla no
  // se queda mostrando solo el trimestre actual.
  const [autoSwitched, setAutoSwitched] = useState(false);
  if (!autoSwitched && plans.length === 0 && viewMode === "plan") {
    setViewMode("period");
    setGranularity("yearly");
    setAutoSwitched(true);
  }

  // When user switches to "period" without prior year/period picks, default to current quarter
  const handleGranularityChange = (g) => {
    setGranularity(g);
    if (g === "monthly") setPeriod(now.getMonth() + 1);
    else if (g === "bimonthly") setPeriod(Math.floor(now.getMonth() / 2) + 1);
    else if (g === "quarterly") setPeriod(Math.floor(now.getMonth() / 3) + 1);
    // yearly: no period needed
  };

  const queryParams = viewMode === "plan"
    ? { mode: "plan", plan_id: planId }
    : granularity === "yearly"
      ? { mode: "period", granularity, year }
      : { mode: "period", granularity, year, period };

  const enabled = viewMode === "period" || (viewMode === "plan" && !!planId);

  const { data: compliance, isLoading } = useQuery({
    queryKey: ["compliance", clientId, viewMode, planId, granularity, year, period],
    queryFn: () => fetchCompliance(clientId, queryParams),
    enabled,
    placeholderData: (prev) => prev,
  });

  // Build period options based on granularity
  const yearsList = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
  const periodOptions = (() => {
    if (granularity === "monthly") {
      return [
        ["Enero", 1], ["Febrero", 2], ["Marzo", 3], ["Abril", 4], ["Mayo", 5], ["Junio", 6],
        ["Julio", 7], ["Agosto", 8], ["Septiembre", 9], ["Octubre", 10], ["Noviembre", 11], ["Diciembre", 12],
      ];
    }
    if (granularity === "bimonthly") {
      return [
        ["Bim 1 · Ene–Feb", 1], ["Bim 2 · Mar–Abr", 2], ["Bim 3 · May–Jun", 3],
        ["Bim 4 · Jul–Ago", 4], ["Bim 5 · Sep–Oct", 5], ["Bim 6 · Nov–Dic", 6],
      ];
    }
    return [["Q1", 1], ["Q2", 2], ["Q3", 3], ["Q4", 4]];
  })();

  const selectedPlan = viewMode === "plan" && planId ? plans.find((p) => p.id === planId) : null;

  return (
    <section data-testid="plan-compliance">
      <div className="mb-4 flex items-start gap-3 flex-wrap">
        <div className="h-9 w-9 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
          <Target className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h2 className="font-display text-lg font-semibold text-slate-900">Ejecución del plan</h2>
          <p className="text-xs text-slate-500 mt-0.5">Volúmenes ejecutados mes a mes, o cumplimiento contra lo comprometido. Filtrá por plan completo o por período.</p>
        </div>
      </div>

      {/* Controls bar */}
      <div className="mb-4 bg-white border border-slate-200 rounded-md p-3 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap" data-testid="compliance-controls">
        {/* View mode toggle */}
        <div className="inline-flex bg-slate-100 border border-slate-200 rounded-md p-0.5" data-testid="view-mode-toggle">
          <button
            onClick={() => setViewMode("plan")}
            data-testid="view-by-plan-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${viewMode === "plan" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
          >
            <FileText className="h-3.5 w-3.5" />
            Por plan
          </button>
          <button
            onClick={() => setViewMode("period")}
            data-testid="view-by-period-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${viewMode === "period" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
          >
            <Calendar className="h-3.5 w-3.5" />
            Por período
          </button>
        </div>

        <div className="h-6 w-px bg-slate-200 hidden sm:block" />

        {/* Sub-controls */}
        {viewMode === "plan" ? (
          plans.length === 0 ? (
            <span className="text-xs text-slate-500 italic">No hay planes configurados. Andá a &quot;Configurar&quot; para crear el primero.</span>
          ) : (
            <Select value={planId || ""} onValueChange={setPlanId}>
              <SelectTrigger className="h-9 w-full sm:w-80 bg-white border-slate-200" data-testid="plan-select">
                <SelectValue placeholder="Elegí un plan" />
              </SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    Plan {p.plan_number} · {p.name} <span className="text-slate-400 ml-1 text-[10px]">({p.start_date}{p.end_date ? ` → ${p.end_date}` : " · sin fin"})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        ) : (
          <>
            <Select value={granularity} onValueChange={handleGranularityChange}>
              <SelectTrigger className="h-9 w-36 bg-white border-slate-200" data-testid="granularity-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yearly">Año completo</SelectItem>
                <SelectItem value="quarterly">Trimestre</SelectItem>
                <SelectItem value="bimonthly">Bimestre</SelectItem>
                <SelectItem value="monthly">Mes</SelectItem>
              </SelectContent>
            </Select>
            {granularity !== "yearly" && (
            <Select value={String(period)} onValueChange={(v) => setPeriod(Number(v))}>
              <SelectTrigger className="h-9 w-44 bg-white border-slate-200" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {periodOptions.map(([label, value]) => (
                  <SelectItem key={value} value={String(value)}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-9 w-28 bg-white border-slate-200" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearsList.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      {isLoading || !compliance ? (
        <div className="h-64 skeleton rounded-md" />
      ) : (
        <>
          {/* Toggle: resumen vs tabla de volúmenes ejecutados mes a mes */}
          <div className="mb-4 inline-flex bg-slate-100 border border-slate-200 rounded-md p-0.5" data-testid="compliance-view-toggle">
            <button
              onClick={() => setComplianceView("volumenes")}
              data-testid="view-volumenes-btn"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${complianceView === "volumenes" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            >
              <TableIcon className="h-3.5 w-3.5" />
              Volúmenes por mes
            </button>
            <button
              onClick={() => setComplianceView("resumen")}
              data-testid="view-resumen-btn"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${complianceView === "resumen" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            >
              <Target className="h-3.5 w-3.5" />
              Cumplimiento vs plan
            </button>
          </div>

          {complianceView === "volumenes" ? (
            <MonthlyVolumeTable data={compliance.monthly_delivered} />
          ) : (
            <ComplianceCard compliance={compliance} viewMode={viewMode} plan={selectedPlan} />
          )}
        </>
      )}
    </section>
  );
}

function MonthlyVolumeTable({ data }) {
  if (!data || !data.rows?.length) {
    return (
      <div className="p-8 text-center text-slate-500 bg-slate-50 rounded-lg border border-slate-200" data-testid="volume-table-empty">
        Sin piezas ejecutadas en el período.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white" data-testid="monthly-volume-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="sticky left-0 z-10 bg-slate-50 text-left px-4 py-2.5 font-semibold text-slate-700 min-w-[220px]">
              Tipo de pieza
            </th>
            <th className="text-right px-3 py-2.5 font-semibold text-slate-700 whitespace-nowrap" data-testid="volume-promised-header">
              Prometido
            </th>
            {data.labels.map((l) => (
              <th key={l} className="text-right px-3 py-2.5 font-semibold text-slate-700 whitespace-nowrap">{l}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {data.rows.map((r) => (
            <tr key={r.type} className="hover:bg-slate-50/50 transition-colors" data-testid={`volume-row-${r.type}`}>
              <td className="sticky left-0 z-10 bg-inherit px-4 py-2 text-slate-800 font-medium">
                {r.label}
                {r.is_extra && (
                  <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-200 whitespace-nowrap">
                    sin compromiso
                  </span>
                )}
              </td>
              <td className="text-right px-3 py-2 tabular-nums whitespace-nowrap" data-testid={`volume-promised-${r.type}`}>
                {r.promised ? (
                  <span className="text-slate-600">
                    {r.promised.amount}
                    <span className="text-slate-400 text-xs">{r.promised.cadence === "mensual" ? "/mes" : " total"}</span>
                  </span>
                ) : (
                  <span className="text-slate-300">—</span>
                )}
              </td>
              {r.by_month.map((n, i) => (
                <td key={i} className={`text-right px-3 py-2 tabular-nums ${n === 0 ? "text-slate-300" : "text-slate-700"}`}>
                  {n === 0 ? "—" : n}
                </td>
              ))}
            </tr>
          ))}
          <tr className="bg-slate-50/80 border-t-2 border-slate-200" data-testid="volume-row-total">
            <td className="sticky left-0 z-10 bg-slate-50 px-4 py-2 font-semibold text-slate-900">Total del mes</td>
            <td className="bg-slate-50/80" />
            {data.totals_by_month.map((n, i) => (
              <td key={i} className="text-right px-3 py-2 font-semibold text-slate-900 tabular-nums">{n || "—"}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ComplianceCard({ compliance, viewMode, plan }) {
  const { label, subtitle, total_target, total_delivered, total_pct, by_type, is_current, is_past, plans_in_range, extra_personal_profiles, months_elapsed, total_months, has_targets, extras_delivered } = compliance;

  // ====== PERIOD VIEW: results-only (no targets) ======
  if (has_targets === false) {
    const stateLabel = is_past ? "Cerrado" : is_current ? "En curso" : "Futuro";
    const stateColor = is_past ? "bg-slate-100 text-slate-600 border-slate-200" : is_current ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200";
    return (
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between mb-6 pb-6 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-sm" data-testid="compliance-label">
                {label}
              </span>
              <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-0.5 rounded-sm border ${stateColor}`} data-testid="compliance-state">
                {stateLabel}
              </span>
            </div>
            <div className="font-display text-3xl font-semibold text-slate-900 tracking-tight mt-2">
              <span className="font-mono tabular-nums" data-testid="compliance-delivered">{total_delivered}</span>
              <span className="text-sm text-slate-500 ml-2 font-sans font-medium">piezas publicadas</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {subtitle && <span className="font-mono">{subtitle}</span>}
            </div>
            {plans_in_range && plans_in_range.length > 0 ? (
              <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-1.5 flex-wrap" data-testid="plans-in-range">
                <span className="text-[9px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Plan(es) que se cruzan:</span>
                {plans_in_range.map((p) => (
                  <span key={p.id} className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-700 font-semibold bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">
                    Plan {p.plan_number} · {p.name}
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-[11px] text-slate-500 italic mt-2" data-testid="no-plan-note">
                Sin plan activo en este período.
              </div>
            )}
            <div className="text-[11px] text-slate-400 italic mt-3 max-w-md">
              En vista por período no mostramos % de cumplimiento porque puede haber dos planes mezclados. Mirá los resultados acá y el % en la vista por plan.
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {by_type.map((t) => {
            const meta = CONTENT_TYPE_META[t.type] || { color: "#64748B", label: t.label };
            return (
              <div key={t.type} className={`rounded-md border p-4 ${t.is_extra ? "border-violet-300 bg-violet-50/40" : "border-slate-200"}`} data-testid={`compliance-${t.type}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="h-2 w-2 rounded-sm shrink-0" style={{ background: meta.color }} />
                  <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold truncate">{meta.label}</span>
                  {t.is_extra && (
                    <span className="text-[9px] font-bold uppercase tracking-wider text-violet-700 bg-violet-100 border border-violet-300 px-1.5 py-0.5 rounded-sm ml-auto" data-testid={`extra-badge-${t.type}`}>
                      Sin compromiso
                    </span>
                  )}
                </div>
                <div className="font-mono text-2xl font-semibold text-slate-900 tabular-nums">
                  {t.delivered}
                </div>
                <div className="text-[10px] text-slate-400 font-mono mt-0.5">publicado</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ====== PLAN VIEW: full target vs delivered with % ======
  const pctClamped = Math.min(100, total_pct || 0);
  const tone = total_pct >= 95 ? "emerald" : total_pct >= 70 ? "sky" : "amber";
  const toneClasses = {
    emerald: { bar: "bg-emerald-500", text: "text-emerald-700", ring: "stroke-emerald-500" },
    sky: { bar: "bg-sky-500", text: "text-sky-700", ring: "stroke-sky-500" },
    amber: { bar: "bg-amber-500", text: "text-amber-700", ring: "stroke-amber-500" },
  }[tone];

  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const dash = (pctClamped / 100) * circumference;

  const stateLabel = is_past ? "Cerrado" : is_current ? "En curso" : "Futuro";
  const stateColor = is_past ? "bg-slate-100 text-slate-600 border-slate-200" : is_current ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200";

  return (
    <div className="bg-white border border-slate-200 rounded-md p-6">
      <div className="flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between mb-6 pb-6 border-b border-slate-100">
        <div className="flex items-center gap-5">
          <div className="relative shrink-0">
            <svg width="128" height="128" viewBox="0 0 128 128" className="-rotate-90">
              <circle cx="64" cy="64" r={radius} fill="none" stroke="#E2E8F0" strokeWidth="10" />
              <circle cx="64" cy="64" r={radius} fill="none" strokeWidth="10" strokeLinecap="round" className={toneClasses.ring} strokeDasharray={`${dash} ${circumference}`} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className={`font-mono text-3xl font-semibold tabular-nums ${toneClasses.text}`} data-testid="compliance-pct">
                {Math.round(total_pct)}%
              </div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-400 font-semibold">cubierto</div>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-sm" data-testid="compliance-label">
                {label}
              </span>
              <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-0.5 rounded-sm border ${stateColor}`} data-testid="compliance-state">
                {stateLabel}
              </span>
              {total_months > 1 && (
                <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-mono">
                  {months_elapsed}/{total_months} {total_months === 1 ? "mes" : "meses"}
                </span>
              )}
            </div>
            <div className="font-display text-3xl font-semibold text-slate-900 tracking-tight mt-2">
              <span className="font-mono tabular-nums" data-testid="compliance-delivered">{total_delivered}</span>
              <span className="text-slate-400 font-mono mx-2">/</span>
              <span className="font-mono text-slate-500" data-testid="compliance-target">{total_target}</span>
              <span className="text-sm text-slate-500 ml-2 font-sans font-medium">piezas prometidas</span>
              {extras_delivered > 0 && (
                <span className="ml-3 align-middle inline-flex items-center gap-1 text-[11px] font-mono font-semibold uppercase tracking-wider text-violet-700 bg-violet-100 border border-violet-300 px-2 py-0.5 rounded-sm" data-testid="compliance-extras">
                  +{extras_delivered} sin compromiso
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {subtitle && <span className="font-mono">{subtitle}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {by_type.map((t) => {
          const meta = CONTENT_TYPE_META[t.type] || { color: "#64748B", label: t.label };
          const pct = Math.min(100, t.pct || 0);
          const isExtra = t.is_extra;
          return (
            <div key={t.type} className={`rounded-md border p-4 ${isExtra ? "border-violet-300 bg-violet-50/40" : "border-slate-200"}`} data-testid={`compliance-${t.type}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="h-2 w-2 rounded-sm shrink-0" style={{ background: meta.color }} />
                <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold truncate">{meta.label}</span>
                {isExtra && (
                  <span className="text-[9px] font-bold uppercase tracking-wider text-violet-700 bg-violet-100 border border-violet-300 px-1.5 py-0.5 rounded-sm ml-auto" data-testid={`extra-badge-${t.type}`}>
                    Sin compromiso
                  </span>
                )}
              </div>
              {isExtra ? (
                <>
                  <div className="font-mono text-xl font-semibold text-violet-900 tabular-nums">
                    +{t.delivered}
                  </div>
                  <div className="text-[10px] text-violet-700 font-mono mt-0.5 uppercase tracking-wider">sin compromiso</div>
                </>
              ) : (
                <>
                  <div className="font-mono text-xl font-semibold text-slate-900 tabular-nums">
                    {t.delivered}<span className="text-slate-400 mx-1">/</span><span className="text-slate-500">{t.target}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-2">
                    <div className={`h-full ${toneClasses.bar}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono mt-1.5">
                    <span>{(t.pct || 0).toFixed(0)}%</span>
                    <span className="text-slate-400">esp. {t.expected_so_far}</span>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {extra_personal_profiles && extra_personal_profiles.length > 0 && (() => {
        const personalRow = by_type.find((t) => t.type === "Personal");
        const totalExtras = extra_personal_profiles.reduce((s, p) => s + (p.total_target || 0), 0);
        const personalBase = Math.max(0, (personalRow?.target || 0) - totalExtras);
        return (
        <div className="mt-6 pt-5 border-t border-slate-100" data-testid="extra-personal-profiles-block">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <User className="h-3.5 w-3.5 text-slate-500" />
            <h4 className="font-display text-sm font-semibold text-slate-900">Composición de Perfil Personal</h4>
            <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">
              {personalBase} (base) + {totalExtras} (extras) = {personalRow?.target || 0} total
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <div className="bg-slate-900 text-white border border-slate-900 rounded-md px-3 py-2 flex items-center justify-between gap-2" data-testid="personal-base-card">
              <div className="min-w-0">
                <div className="text-xs font-medium truncate">Perfil Personal (base)</div>
                <div className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">del plan</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-sm font-semibold tabular-nums">+{personalBase}</div>
                <div className="text-[9px] text-slate-400 font-mono uppercase tracking-wider">en el plan</div>
              </div>
            </div>
            {extra_personal_profiles.map((prof, idx) => (
              <div key={idx} className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 flex items-center justify-between gap-2" data-testid={`extra-profile-card-${idx}`}>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-slate-800 truncate">{prof.name}</div>
                  <div className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">perfil extra</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm font-semibold text-slate-900 tabular-nums">+{prof.total_target}</div>
                  <div className="text-[9px] text-slate-400 font-mono uppercase tracking-wider">en el plan</div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 italic mt-3">
            Nota: las publicaciones se cuentan agregadas como &quot;Perfil Personal&quot; — no diferenciamos por persona en el backlog todavía.
          </div>
        </div>
        );
      })()}

      {plan && <PlanMetricsBlock plan={plan} />}
    </div>
  );
}

/* ========== Section 2: CRECIMIENTO ========== */
function CrecimientoSection({ clientId, metricoolConnected }) {
  if (!metricoolConnected) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center">
        <TrendingUp className="h-8 w-8 text-slate-400 mx-auto mb-3" />
        <h3 className="font-display text-base font-semibold text-slate-900 mb-1">Conectá Metricool</h3>
        <p className="text-sm text-slate-500">Sin Metricool no tenemos datos de evolución para mostrar.</p>
      </div>
    );
  }
  return (
    <div className="space-y-10">
      <CorporateGrowth clientId={clientId} />
      <PersonalGrowth clientId={clientId} />
    </div>
  );
}

function CorporateGrowth({ clientId }) {
  const [viewModeRaw, setViewMode] = useState("plan");
  const [planId, setPlanIdRaw] = useState(null);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [yearTo, setYearTo] = useState(now.getFullYear());
  const [monthTo, setMonthTo] = useState(now.getMonth() + 1);

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ["comms-plans", clientId],
    queryFn: () => fetchCommsPlans(clientId),
  });

  // Derived: if no plans exist, force "period" view (no useEffect needed).
  const viewMode = (!plansLoading && plans.length === 0 && viewModeRaw === "plan") ? "period" : viewModeRaw;
  // Derived: default plan_id = first plan if not picked yet.
  const effectivePlanId = planId || (plans[0]?.id ?? null);
  const setPlanId = setPlanIdRaw;

  const params = viewMode === "plan"
    ? { mode: "plan", plan_id: effectivePlanId }
    : { mode: "period", year, period: month, year_to: yearTo, month_to: monthTo };

  const { data, isLoading } = useQuery({
    queryKey: ["growth-corporate", clientId, viewMode, effectivePlanId, year, month, yearTo, monthTo],
    queryFn: () => fetchGrowthCorporate(clientId, params),
    enabled: viewMode === "period" || !!effectivePlanId,
    placeholderData: (prev) => prev,
  });

  const yearsList = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
  const monthOptions = [
    ["Ene", 1], ["Feb", 2], ["Mar", 3], ["Abr", 4], ["May", 5], ["Jun", 6],
    ["Jul", 7], ["Ago", 8], ["Sep", 9], ["Oct", 10], ["Nov", 11], ["Dic", 12],
  ];

  return (
    <div className="space-y-6" data-testid="corporate-growth">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-9 w-9 rounded-md bg-blue-600 text-white flex items-center justify-center shrink-0">
          <Briefcase className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h2 className="font-display text-lg font-semibold text-slate-900">Crecimiento · Perfil Corporativo</h2>
          <p className="text-xs text-slate-500 mt-0.5">LinkedIn Company Page · Metricool en tiempo real</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap" data-testid="growth-controls">
        <div className="inline-flex bg-slate-100 border border-slate-200 rounded-md p-0.5">
          <button onClick={() => setViewMode("plan")} data-testid="growth-by-plan-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${viewMode === "plan" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
            <FileText className="h-3.5 w-3.5" />Por plan
          </button>
          <button onClick={() => setViewMode("period")} data-testid="growth-by-period-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${viewMode === "period" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
            <Calendar className="h-3.5 w-3.5" />Por rango de meses
          </button>
        </div>
        <div className="h-6 w-px bg-slate-200 hidden sm:block" />
        {viewMode === "plan" ? (
          plans.length === 0 ? (
            <span className="text-xs text-slate-500 italic">Sin planes configurados.</span>
          ) : (
            <Select value={effectivePlanId || ""} onValueChange={setPlanId}>
              <SelectTrigger className="h-9 w-full sm:w-80 bg-white border-slate-200" data-testid="growth-plan-select">
                <SelectValue placeholder="Elegí un plan" />
              </SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    Plan {p.plan_number} · {p.name} <span className="text-slate-400 ml-1 text-[10px]">({p.start_date}{p.end_date ? ` → ${p.end_date}` : ""})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Desde</span>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="h-9 w-24 bg-white border-slate-200" data-testid="growth-month-from"><SelectValue /></SelectTrigger>
              <SelectContent>{monthOptions.map(([l, v]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-9 w-24 bg-white border-slate-200" data-testid="growth-year-from"><SelectValue /></SelectTrigger>
              <SelectContent>{yearsList.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold ml-2">Hasta</span>
            <Select value={String(monthTo)} onValueChange={(v) => setMonthTo(Number(v))}>
              <SelectTrigger className="h-9 w-24 bg-white border-slate-200" data-testid="growth-month-to"><SelectValue /></SelectTrigger>
              <SelectContent>{monthOptions.map(([l, v]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(yearTo)} onValueChange={(v) => setYearTo(Number(v))}>
              <SelectTrigger className="h-9 w-24 bg-white border-slate-200" data-testid="growth-year-to"><SelectValue /></SelectTrigger>
              <SelectContent>{yearsList.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>

      {isLoading || !data ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-32 skeleton rounded-md" />)}
        </div>
      ) : (
        <GrowthCard data={data} />
      )}
    </div>
  );
}

function GrowthCard({ data }) {
  const { label, subtitle, comunidad, cuenta, contenido_visto, publicaciones, posts_list } = data;
  const fmt = (n) => {
    if (n == null) return "—";
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 2)}k`;
    return n.toLocaleString();
  };

  const renderContentGrid = (block, testIdPrefix) => {
    if (!block) return <div className="text-xs text-slate-400 italic">Sin datos</div>;
    const metrics = [
      { key: "impresiones", label: "Impresiones", color: "#6366F1", icon: Eye },
      { key: "interacciones", label: "Interacciones", color: "#F59E0B", icon: Target },
      { key: "reacciones", label: "Reacciones", color: "#10B981", icon: Heart },
      { key: "comentarios", label: "Comentarios", color: "#EC4899", icon: MessageCircle },
      { key: "compartidos", label: "Compartidos", color: "#8B5CF6", icon: Share2 },
      { key: "clicks", label: "Clics", color: "#0EA5E9", icon: ExternalLink },
    ];
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {metrics.map((m) => {
          const Icon = m.icon;
          const val = block[m.key] ?? 0;
          return (
            <div key={m.key} className="rounded-md p-4 border" style={{ background: `${m.color}0A`, borderColor: `${m.color}33` }} data-testid={`${testIdPrefix}-${m.key}`}>
              <div className="flex items-center gap-1.5 mb-2">
                <span className="h-6 w-6 rounded-md flex items-center justify-center shrink-0" style={{ background: `${m.color}22`, color: m.color }}>
                  <Icon className="h-3 w-3" />
                </span>
                <span className="text-[10px] uppercase tracking-[0.16em] text-slate-600 font-semibold truncate">{m.label}</span>
              </div>
              <div className="font-mono text-2xl font-semibold tabular-nums" style={{ color: m.color }}>{fmt(val)}</div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-2.5 flex items-center gap-2 flex-wrap" data-testid="growth-period-band">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold bg-white border border-slate-200 px-2 py-0.5 rounded-sm">{label}</span>
        <span className="text-[11px] text-slate-500 font-mono">{subtitle}</span>
      </div>

      {/* Comunidad */}
      <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="growth-comunidad">
        <div className="flex items-start gap-2 mb-3 flex-wrap">
          <h3 className="font-display text-sm font-semibold text-slate-900">Comunidad</h3>
          <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Total de seguidores (snapshot global)</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">Seguidores totales hoy</div>
            <div className="font-mono text-2xl font-semibold text-slate-900 tabular-nums">{fmt(comunidad?.total_followers)}</div>
          </div>
        </div>
      </section>

      {/* Cuenta */}
      <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="growth-cuenta">
        <div className="flex items-start gap-2 mb-3 flex-wrap">
          <h3 className="font-display text-sm font-semibold text-slate-900">Cuenta · Crecimiento del período</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            ["Visitantes únicos", cuenta?.unique_visitors],
            ["Visualizaciones de página", cuenta?.page_views],
            ["Nuevos seguidores", cuenta?.new_followers],
          ].map(([lbl, val]) => (
            <div key={lbl} className="bg-slate-50 border border-slate-200 rounded-md p-4">
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">{lbl}</div>
              <div className="font-mono text-2xl font-semibold text-slate-900 tabular-nums">{fmt(val)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Contenido visto en el período (account-level) */}
      <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="growth-contenido-visto">
        <div className="flex items-start gap-2 mb-3 flex-wrap">
          <h3 className="font-display text-sm font-semibold text-slate-900">Contenido visto en el período</h3>
          <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">
            Todo lo que la página recibió (página + posts + actividad orgánica)
          </span>
        </div>
        {renderContentGrid(contenido_visto, "cv")}
      </section>

      {/* Publicaciones del período (posts-level) */}
      <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="growth-publicaciones">
        <div className="flex items-start gap-2 mb-3 flex-wrap">
          <h3 className="font-display text-sm font-semibold text-slate-900">Publicaciones del período</h3>
          <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">
            Solo los {publicaciones?.posts || 0} posts publicados en este rango
          </span>
        </div>
        {renderContentGrid(publicaciones, "pub")}
      </section>
    </div>
  );
}

function PersonalGrowth({ clientId }) {
  const [viewModeRaw, setViewMode] = useState("plan");
  const [planId, setPlanIdRaw] = useState(null);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [yearTo, setYearTo] = useState(now.getFullYear());
  const [monthTo, setMonthTo] = useState(now.getMonth() + 1);

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ["comms-plans", clientId],
    queryFn: () => fetchCommsPlans(clientId),
  });

  const viewMode = (!plansLoading && plans.length === 0 && viewModeRaw === "plan") ? "period" : viewModeRaw;
  const effectivePlanId = planId || (plans[0]?.id ?? null);
  const setPlanId = setPlanIdRaw;

  const params = viewMode === "plan"
    ? { mode: "plan", plan_id: effectivePlanId }
    : { mode: "period", year, period: month, year_to: yearTo, month_to: monthTo };

  const { data, isLoading } = useQuery({
    queryKey: ["growth-personal", clientId, viewMode, effectivePlanId, year, month, yearTo, monthTo],
    queryFn: () => fetchGrowthPersonal(clientId, params),
    enabled: viewMode === "period" || !!effectivePlanId,
    placeholderData: (prev) => prev,
  });

  const yearsList = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
  const monthOptions = [
    ["Ene", 1], ["Feb", 2], ["Mar", 3], ["Abr", 4], ["May", 5], ["Jun", 6],
    ["Jul", 7], ["Ago", 8], ["Sep", 9], ["Oct", 10], ["Nov", 11], ["Dic", 12],
  ];

  return (
    <div className="space-y-6" data-testid="personal-growth">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-9 w-9 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
          <User className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h2 className="font-display text-lg font-semibold text-slate-900">Crecimiento · Perfil Personal</h2>
          <p className="text-xs text-slate-500 mt-0.5">LinkedIn personal · Metricool en tiempo real</p>
        </div>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2.5" data-testid="personal-disclaimer">
        <Info className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-900 leading-relaxed">
          <span className="font-semibold">Importante:</span> Las métricas solo incluyen publicaciones hechas <span className="font-semibold">desde Metricool</span>.
          Los reposts (que no se pueden hacer desde Metricool) no aparecen acá como métricas. Igualmente, en la tabla de publicaciones tenemos el link directo a LinkedIn para revisarlos manualmente cuando estén disponibles.
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap" data-testid="personal-growth-controls">
        <div className="inline-flex bg-slate-100 border border-slate-200 rounded-md p-0.5">
          <button onClick={() => setViewMode("plan")} data-testid="personal-by-plan-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${viewMode === "plan" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
            <FileText className="h-3.5 w-3.5" />Por plan
          </button>
          <button onClick={() => setViewMode("period")} data-testid="personal-by-period-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${viewMode === "period" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
            <Calendar className="h-3.5 w-3.5" />Por rango de meses
          </button>
        </div>
        <div className="h-6 w-px bg-slate-200 hidden sm:block" />
        {viewMode === "plan" ? (
          plans.length === 0 ? (
            <span className="text-xs text-slate-500 italic">Sin planes configurados.</span>
          ) : (
            <Select value={effectivePlanId || ""} onValueChange={setPlanId}>
              <SelectTrigger className="h-9 w-full sm:w-80 bg-white border-slate-200" data-testid="personal-plan-select">
                <SelectValue placeholder="Elegí un plan" />
              </SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    Plan {p.plan_number} · {p.name} <span className="text-slate-400 ml-1 text-[10px]">({p.start_date}{p.end_date ? ` → ${p.end_date}` : ""})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Desde</span>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="h-9 w-24 bg-white border-slate-200" data-testid="personal-month-from"><SelectValue /></SelectTrigger>
              <SelectContent>{monthOptions.map(([l, v]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-9 w-24 bg-white border-slate-200" data-testid="personal-year-from"><SelectValue /></SelectTrigger>
              <SelectContent>{yearsList.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold ml-2">Hasta</span>
            <Select value={String(monthTo)} onValueChange={(v) => setMonthTo(Number(v))}>
              <SelectTrigger className="h-9 w-24 bg-white border-slate-200" data-testid="personal-month-to"><SelectValue /></SelectTrigger>
              <SelectContent>{monthOptions.map(([l, v]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(yearTo)} onValueChange={(v) => setYearTo(Number(v))}>
              <SelectTrigger className="h-9 w-24 bg-white border-slate-200" data-testid="personal-year-to"><SelectValue /></SelectTrigger>
              <SelectContent>{yearsList.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>

      {isLoading || !data ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-32 skeleton rounded-md" />)}
        </div>
      ) : (data.personals || []).length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center">
          <User className="h-7 w-7 text-slate-400 mx-auto mb-2" />
          <h3 className="font-display text-sm font-semibold text-slate-900 mb-1">Sin perfil personal vinculado</h3>
          <p className="text-xs text-slate-500">Vinculá un perfil personal de LinkedIn desde Metricool para ver métricas acá.</p>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-2.5 flex items-center gap-2 flex-wrap" data-testid="personal-period-band">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold bg-white border border-slate-200 px-2 py-0.5 rounded-sm">{data.label}</span>
            <span className="text-[11px] text-slate-500 font-mono">{data.subtitle}</span>
          </div>
          {(data.personals || []).map((person) => (
            <PersonalProfileCard key={person.mapping_id || person.blog_id} person={person} />
          ))}
        </div>
      )}
    </div>
  );
}

function PersonalProfileCard({ person }) {
  const { label, picture, linkedin_url, comunidad, cuenta, contenido_visto, publicaciones } = person;
  const fmt = (n) => {
    if (n == null) return "—";
    if (typeof n === "number" && !Number.isInteger(n)) return n.toLocaleString("es-AR", { maximumFractionDigits: 2 });
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 2)}k`;
    return n.toLocaleString();
  };

  const renderContentGrid = (block, testIdPrefix) => {
    if (!block) return <div className="text-xs text-slate-400 italic">Sin datos</div>;
    const metrics = [
      { key: "impresiones", label: "Impresiones", color: "#6366F1", icon: Eye },
      { key: "interacciones", label: "Interacciones", color: "#F59E0B", icon: Target },
      { key: "reacciones", label: "Reacciones", color: "#10B981", icon: Heart },
      { key: "comentarios", label: "Comentarios", color: "#EC4899", icon: MessageCircle },
      { key: "compartidos", label: "Compartidos", color: "#8B5CF6", icon: Share2 },
    ];
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {metrics.map((m) => {
          const Icon = m.icon;
          const val = block[m.key] ?? 0;
          return (
            <div key={m.key} className="rounded-md p-4 border" style={{ background: `${m.color}0A`, borderColor: `${m.color}33` }} data-testid={`${testIdPrefix}-${m.key}`}>
              <div className="flex items-center gap-1.5 mb-2">
                <span className="h-6 w-6 rounded-md flex items-center justify-center shrink-0" style={{ background: `${m.color}22`, color: m.color }}>
                  <Icon className="h-3 w-3" />
                </span>
                <span className="text-[10px] uppercase tracking-[0.16em] text-slate-600 font-semibold truncate">{m.label}</span>
              </div>
              <div className="font-mono text-2xl font-semibold tabular-nums" style={{ color: m.color }}>{fmt(val)}</div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid={`personal-profile-${person.blog_id}`}>
      <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-3 flex-wrap">
        {picture ? (
          <img src={picture} alt={label} className="h-12 w-12 rounded-full object-cover border border-slate-200" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center"><User className="h-5 w-5 text-slate-400" /></div>
        )}
        <div className="flex-1 min-w-[160px]">
          <h3 className="font-display text-base font-semibold text-slate-900">{label}</h3>
          <p className="text-[11px] text-slate-500 font-mono">blog_id · {person.blog_id}</p>
        </div>
        {linkedin_url && (
          <a href={linkedin_url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:text-blue-900 border border-blue-200 hover:border-blue-300 bg-blue-50 hover:bg-blue-100 px-2.5 py-1.5 rounded-md transition-colors"
            data-testid={`personal-linkedin-link-${person.blog_id}`}>
            <ExternalLink className="h-3.5 w-3.5" />Ver en LinkedIn
          </a>
        )}
      </div>

      {/* Comunidad */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-start gap-2 mb-3 flex-wrap">
          <h3 className="font-display text-sm font-semibold text-slate-900">Comunidad</h3>
          <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Snapshot actual y ritmo de crecimiento</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">Seguidores totales hoy</div>
            <div className="font-mono text-2xl font-semibold text-slate-900 tabular-nums">{fmt(comunidad?.total_followers)}</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">Seguidores diarios (prom.)</div>
            <div className="font-mono text-2xl font-semibold text-slate-900 tabular-nums">{fmt(comunidad?.followers_per_day)}</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">Seguidores por contenido</div>
            <div className="font-mono text-2xl font-semibold text-slate-900 tabular-nums">{fmt(comunidad?.followers_per_post)}</div>
          </div>
        </div>
      </section>

      {/* Cuenta */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-start gap-2 mb-3 flex-wrap">
          <h3 className="font-display text-sm font-semibold text-slate-900">Cuenta · Crecimiento del período</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">Nuevos seguidores</div>
            <div className="font-mono text-2xl font-semibold text-slate-900 tabular-nums">{fmt(cuenta?.new_followers)}</div>
          </div>
        </div>
      </section>

      {/* Contenido visto */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-start gap-2 mb-3 flex-wrap">
          <h3 className="font-display text-sm font-semibold text-slate-900">Contenido visto en el período</h3>
          <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">
            Actividad total sobre el perfil (posts propios + interacciones recibidas)
          </span>
        </div>
        {renderContentGrid(contenido_visto, `pcv-${person.blog_id}`)}
      </section>

      {/* Publicaciones */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-start gap-2 mb-3 flex-wrap">
          <h3 className="font-display text-sm font-semibold text-slate-900">Publicaciones del período</h3>
          <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">
            Solo los {publicaciones?.posts || 0} posts publicados desde Metricool en este rango
          </span>
        </div>
        {renderContentGrid(publicaciones, `ppub-${person.blog_id}`)}
      </section>
    </div>
  );
}



function PostsTable({ posts, showOrigin = false }) {
  if (!posts || posts.length === 0) {
    return (
      <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="posts-table-empty">
        <h3 className="font-display text-sm font-semibold text-slate-900 mb-2">Lista de publicaciones</h3>
        <p className="text-xs text-slate-500 italic">Sin publicaciones en este período.</p>
      </section>
    );
  }
  const fmt = (n) => (n == null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : n.toLocaleString());
  const originBadge = (origin, label) => {
    if (!origin) return null;
    const isCorpo = origin === "Corpo";
    return (
      <span
        title={label}
        className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm border ${isCorpo ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-900 text-white border-slate-900"}`}
      >
        {isCorpo ? <Briefcase className="h-2.5 w-2.5" /> : <User className="h-2.5 w-2.5" />}
        {isCorpo ? "Corpo" : "Personal"}
      </span>
    );
  };
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="posts-table">
      <div className="flex items-start gap-2 mb-4 flex-wrap">
        <h3 className="font-display text-sm font-semibold text-slate-900">Lista de publicaciones</h3>
        <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">{posts.length} posts · ordenados por fecha desc</span>
      </div>
      <div className="overflow-x-auto -mx-5">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-y border-slate-200">
            <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
              <th className="px-3 py-2 font-semibold">Post</th>
              {showOrigin && <th className="px-2 py-2 font-semibold">Origen</th>}
              <th className="px-2 py-2 font-semibold">Tipo</th>
              <th className="px-2 py-2 font-semibold">Fecha</th>
              <th className="px-2 py-2 font-semibold text-right">Impr.</th>
              <th className="px-2 py-2 font-semibold text-right">React.</th>
              <th className="px-2 py-2 font-semibold text-right">Coment.</th>
              <th className="px-2 py-2 font-semibold text-right">Comp.</th>
              <th className="px-2 py-2 font-semibold text-right">Clicks</th>
              <th className="px-2 py-2 font-semibold text-right">Eng.%</th>
              <th className="px-2 py-2 font-semibold"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {posts.map((p) => (
              <tr key={`${p._origin || ""}-${p.id}`} className="hover:bg-slate-50" data-testid={`post-row-${p.id}`}>
                <td className="px-3 py-2 max-w-[420px]">
                  <div className="flex gap-2 items-start">
                    {p.image ? (
                      <img src={p.image} alt="" className="w-10 h-10 rounded object-cover shrink-0 border border-slate-200" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} />
                    ) : (
                      <div className="w-10 h-10 rounded bg-slate-100 shrink-0 flex items-center justify-center"><FileText className="h-4 w-4 text-slate-400" /></div>
                    )}
                    <div className="text-slate-700 line-clamp-2 leading-snug min-w-0">{p.text || <span className="text-slate-400 italic">(sin texto)</span>}</div>
                  </div>
                </td>
                {showOrigin && (
                  <td className="px-2 py-2 whitespace-nowrap">{originBadge(p._origin, p._origin_label)}</td>
                )}
                <td className="px-2 py-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">{p.type || "—"}</td>
                <td className="px-2 py-2 font-mono text-[10px] text-slate-500 whitespace-nowrap">{p.published_at ? p.published_at.slice(0, 10) : "—"}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-900">{fmt(p.impressions)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-emerald-700">{fmt(p.reactions)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-pink-700">{fmt(p.comments)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-violet-700">{fmt(p.shares)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-sky-700">{fmt(p.clicks)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{(p.engagement || 0).toFixed(2)}</td>
                <td className="px-2 py-2">
                  {p.url && (
                    <a href={p.url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-slate-700 inline-flex" data-testid={`post-link-${p.id}`}>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ProfileSummaryCard({ title, icon: Icon, color, totals, testId }) {
  if (!totals) return null;
  const growth = totals.growth_pct_3m;
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-md p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-md flex items-center justify-center text-white" style={{ background: color }}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{title}</div>
          <div className="font-mono text-xs text-slate-400">últimos 6 meses</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Stat label="Publicaciones" value={totals.posts} />
        <Stat label="Alcance total" value={totals.reach} />
        <Stat label="Likes" value={totals.likes} />
        <Stat label="Shares" value={totals.shares} />
      </div>
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Alcance promedio / post</div>
          <div className="font-mono text-xl font-semibold text-slate-900 tabular-nums">{formatNumber(totals.avg_reach_per_post)}</div>
        </div>
        {growth !== null && growth !== undefined && (
          <div className={`text-right`}>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Crecimiento 3m</div>
            <div className={`font-mono text-xl font-semibold tabular-nums ${growth >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
              {growth >= 0 ? "+" : ""}{growth}%
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">{label}</div>
      <div className="font-mono text-lg font-semibold text-slate-900 tabular-nums">{formatNumber(value)}</div>
    </div>
  );
}

function ReachEvolutionChart({ data }) {
  return (
    <div className="w-full" style={{ height: 280 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 12, right: 12, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 6" stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
          <YAxis tick={{ fill: "#94A3B8", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} axisLine={false} width={48} tickFormatter={(v) => v >= 1000 ? `${v/1000}k` : v} />
          <Tooltip content={<DarkTooltip />} cursor={{ fill: "#F1F5F9" }} />
          <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Sans", color: "#64748B", paddingTop: 8 }} iconType="square" iconSize={8} />
          <Bar dataKey="Corpo_reach" name="Corporativo" fill="#2563EB" radius={[3, 3, 0, 0]} isAnimationActive={false} />
          <Bar dataKey="Personal_reach" name="Personal" fill="#0F172A" radius={[3, 3, 0, 0]} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function PostsEvolutionChart({ data }) {
  return (
    <div className="w-full" style={{ height: 240 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 12, right: 12, left: -10, bottom: 0 }} barCategoryGap={"20%"}>
          <CartesianGrid strokeDasharray="3 6" stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
          <YAxis tick={{ fill: "#94A3B8", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} axisLine={false} width={36} />
          <Tooltip content={<DarkTooltip />} cursor={{ fill: "#F1F5F9" }} />
          <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Sans", color: "#64748B", paddingTop: 8 }} iconType="square" iconSize={8} />
          <Bar dataKey="Corpo_posts" name="Corporativo" fill="#2563EB" radius={[3, 3, 0, 0]} isAnimationActive={false} />
          <Bar dataKey="Personal_posts" name="Personal" fill="#0F172A" radius={[3, 3, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function AvgReachChart({ data }) {
  return (
    <div className="w-full" style={{ height: 240 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 12, right: 12, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 6" stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
          <YAxis tick={{ fill: "#94A3B8", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} axisLine={false} width={48} tickFormatter={(v) => v >= 1000 ? `${v/1000}k` : v} />
          <Tooltip content={<DarkTooltip />} cursor={{ stroke: "#CBD5E1", strokeDasharray: "3 3" }} />
          <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Sans", color: "#64748B", paddingTop: 8 }} iconType="square" iconSize={8} />
          <Line type="monotone" dataKey="Corpo_avg_reach" name="Corpo avg" stroke="#2563EB" strokeWidth={2} dot={{ r: 3, strokeWidth: 0, fill: "#2563EB" }} isAnimationActive={false} />
          <Line type="monotone" dataKey="Personal_avg_reach" name="Personal avg" stroke="#0F172A" strokeWidth={2} dot={{ r: 3, strokeWidth: 0, fill: "#0F172A" }} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function DarkTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-slate-950 text-white text-xs rounded-md p-3 shadow-md border border-slate-800 font-mono min-w-[160px]">
      <div className="text-slate-400 mb-1.5 uppercase tracking-wider text-[10px]">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 tabular-nums">
          <span className="w-2 h-2 rounded-sm" style={{ background: p.color }} />
          <span className="text-slate-300">{p.name}:</span>
          <span className="text-white font-semibold ml-auto">{Number(p.value).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

/* ========== Section 3: ANÁLISIS METRICOOL ========== */
function AnalisisSection({ clientId, metricoolConnected }) {
  if (!metricoolConnected) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center">
        <BarChart3 className="h-8 w-8 text-slate-400 mx-auto mb-3" />
        <h3 className="font-display text-base font-semibold text-slate-900">Conectá Metricool</h3>
        <p className="text-sm text-slate-500">Sin Metricool no podemos mostrar el análisis de publicaciones.</p>
      </div>
    );
  }
  return <PostsAnalysis clientId={clientId} />;
}

function PostsAnalysis({ clientId }) {
  const [viewModeRaw, setViewMode] = useState("plan");
  const [planId, setPlanIdRaw] = useState(null);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [yearTo, setYearTo] = useState(now.getFullYear());
  const [monthTo, setMonthTo] = useState(now.getMonth() + 1);
  const [originFilter, setOriginFilter] = useState("all"); // all | corpo | personal
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ["comms-plans", clientId],
    queryFn: () => fetchCommsPlans(clientId),
  });

  const viewMode = (!plansLoading && plans.length === 0 && viewModeRaw === "plan") ? "period" : viewModeRaw;
  const effectivePlanId = planId || (plans[0]?.id ?? null);
  const setPlanId = setPlanIdRaw;

  const params = viewMode === "plan"
    ? { mode: "plan", plan_id: effectivePlanId }
    : { mode: "period", year, period: month, year_to: yearTo, month_to: monthTo };

  const queryEnabled = viewMode === "period" || !!effectivePlanId;
  const { data: corpoData } = useQuery({
    queryKey: ["growth-corporate", clientId, viewMode, effectivePlanId, year, month, yearTo, monthTo],
    queryFn: () => fetchGrowthCorporate(clientId, params),
    enabled: queryEnabled,
    placeholderData: (prev) => prev,
  });
  const { data: personalData } = useQuery({
    queryKey: ["growth-personal", clientId, viewMode, effectivePlanId, year, month, yearTo, monthTo],
    queryFn: () => fetchGrowthPersonal(clientId, params),
    enabled: queryEnabled,
    placeholderData: (prev) => prev,
  });

  const corpoPosts = (corpoData?.posts_list || []).map((p) => ({ ...p, _origin: "Corpo", _origin_label: "Corporativo" }));
  const personalPosts = ((personalData?.personals) || []).flatMap((person) =>
    (person.posts_list || []).map((p) => ({ ...p, _origin: "Personal", _origin_label: person.label || "Personal" }))
  );
  const sourcePosts = originFilter === "corpo"
    ? corpoPosts
    : originFilter === "personal"
    ? personalPosts
    : [...corpoPosts, ...personalPosts];

  const types = Array.from(new Set(sourcePosts.map((p) => p.type).filter(Boolean))).sort();

  const filtered = sourcePosts
    .filter((p) => typeFilter === "all" || p.type === typeFilter)
    .filter((p) => !search.trim() || (p.text || "").toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const getter = (p) => {
        if (sortBy === "date") return p.published_at || "";
        if (sortBy === "impressions") return p.impressions || 0;
        if (sortBy === "reactions") return p.reactions || 0;
        if (sortBy === "comments") return p.comments || 0;
        if (sortBy === "shares") return p.shares || 0;
        if (sortBy === "clicks") return p.clicks || 0;
        if (sortBy === "engagement") return p.engagement || 0;
        return 0;
      };
      const va = getter(a), vb = getter(b);
      const cmp = va > vb ? 1 : va < vb ? -1 : 0;
      return sortDir === "desc" ? -cmp : cmp;
    });

  const yearsList = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
  const monthOptions = [["Ene", 1], ["Feb", 2], ["Mar", 3], ["Abr", 4], ["May", 5], ["Jun", 6], ["Jul", 7], ["Ago", 8], ["Sep", 9], ["Oct", 10], ["Nov", 11], ["Dic", 12]];

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  return (
    <section data-testid="posts-analysis">
      <div className="mb-4 flex items-start gap-3 flex-wrap">
        <div className="h-9 w-9 rounded-md bg-indigo-600 text-white flex items-center justify-center shrink-0">
          <BarChart3 className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h2 className="font-display text-lg font-semibold text-slate-900">Análisis de publicaciones</h2>
          <p className="text-xs text-slate-500 mt-0.5">Posts corporativos + perfiles personales en una sola tabla</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap mb-3">
        <div className="inline-flex bg-slate-100 border border-slate-200 rounded-md p-0.5">
          <button onClick={() => setViewMode("plan")} data-testid="pa-by-plan-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${viewMode === "plan" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
            <FileText className="h-3.5 w-3.5" />Por plan
          </button>
          <button onClick={() => setViewMode("period")} data-testid="pa-by-period-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${viewMode === "period" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
            <Calendar className="h-3.5 w-3.5" />Por rango
          </button>
        </div>
        {viewMode === "plan" ? (
          plans.length > 0 && (
            <Select value={effectivePlanId || ""} onValueChange={setPlanId}>
              <SelectTrigger className="h-9 w-full sm:w-72 bg-white border-slate-200" data-testid="pa-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>Plan {p.plan_number} · {p.name}</SelectItem>)}</SelectContent>
            </Select>
          )
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Desde</span>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}><SelectTrigger className="h-9 w-20 bg-white"><SelectValue /></SelectTrigger><SelectContent>{monthOptions.map(([l, v]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent></Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}><SelectTrigger className="h-9 w-24 bg-white"><SelectValue /></SelectTrigger><SelectContent>{yearsList.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
            <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Hasta</span>
            <Select value={String(monthTo)} onValueChange={(v) => setMonthTo(Number(v))}><SelectTrigger className="h-9 w-20 bg-white"><SelectValue /></SelectTrigger><SelectContent>{monthOptions.map(([l, v]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent></Select>
            <Select value={String(yearTo)} onValueChange={(v) => setYearTo(Number(v))}><SelectTrigger className="h-9 w-24 bg-white"><SelectValue /></SelectTrigger><SelectContent>{yearsList.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3 flex-wrap mb-3">
        <div className="inline-flex bg-slate-100 border border-slate-200 rounded-md p-0.5" data-testid="pa-origin-toggle">
          {[["all", `Todos (${corpoPosts.length + personalPosts.length})`], ["corpo", `Corpo (${corpoPosts.length})`], ["personal", `Personal (${personalPosts.length})`]].map(([v, l]) => (
            <button key={v} onClick={() => setOriginFilter(v)} data-testid={`pa-origin-${v}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${originFilter === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="h-6 w-px bg-slate-200" />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-9 w-44 bg-white" data-testid="pa-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input placeholder="Buscar en el texto del post…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-9 bg-white" data-testid="pa-search" />
        </div>
        <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-mono">{filtered.length} de {sourcePosts.length}</span>
      </div>

      <TopPerformersGrid posts={filtered} />

      <PostsTable posts={filtered} onSort={toggleSort} sortBy={sortBy} sortDir={sortDir} showOrigin />
    </section>
  );
}

function TopPerformersGrid({ posts }) {
  // Top 6 by engagement; if all engagement is 0, fall back to impressions.
  const ranked = [...posts]
    .filter((p) => (p.impressions || 0) + (p.reactions || 0) + (p.comments || 0) + (p.shares || 0) > 0)
    .sort((a, b) => {
      const ea = a.engagement || 0, eb = b.engagement || 0;
      if (eb !== ea) return eb - ea;
      return (b.impressions || 0) - (a.impressions || 0);
    })
    .slice(0, 6);

  if (ranked.length === 0) return null;

  const fmt = (n) => (n == null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : n.toLocaleString());

  return (
    <section className="mb-5" data-testid="top-performers">
      <div className="flex items-end justify-between gap-2 flex-wrap mb-3">
        <div>
          <h3 className="font-display text-sm font-semibold text-slate-900">Top performers</h3>
          <p className="text-xs text-slate-500 mt-0.5">Los 6 posts con mejor engagement del filtro actual</p>
        </div>
        <span className="text-[10px] uppercase tracking-[0.16em] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm font-semibold inline-flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Metricool · live
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {ranked.map((p, idx) => (
          <a
            key={`${p._origin}-${p.id}`}
            href={p.url || "#"}
            target={p.url ? "_blank" : undefined}
            rel="noreferrer"
            data-testid={`top-perf-${p.id}`}
            className="group bg-white border border-slate-200 rounded-md p-4 hover:border-slate-400 hover:shadow-sm transition-all flex flex-col gap-3 relative"
          >
            <div className="absolute -top-2 -left-2 h-6 w-6 rounded-full bg-slate-900 text-white text-[11px] font-mono font-semibold flex items-center justify-center shadow-sm">
              {idx + 1}
            </div>
            <div className="flex items-center justify-between gap-2">
              <span
                className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm border ${
                  p._origin === "Corpo"
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-slate-900 text-white border-slate-900"
                }`}
              >
                {p._origin === "Corpo" ? <Briefcase className="h-2.5 w-2.5" /> : <User className="h-2.5 w-2.5" />}
                {p._origin === "Corpo" ? "Corpo" : "Personal"}
              </span>
              <span className="text-[10px] font-mono text-slate-400">{p.published_at ? p.published_at.slice(0, 10) : "—"}</span>
            </div>
            <div className="flex gap-2.5 items-start">
              {p.image ? (
                <img src={p.image} alt="" className="w-12 h-12 rounded object-cover shrink-0 border border-slate-200" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <div className="w-12 h-12 rounded bg-slate-100 shrink-0 flex items-center justify-center"><FileText className="h-4 w-4 text-slate-400" /></div>
              )}
              <div className="text-xs text-slate-700 line-clamp-3 leading-snug min-w-0">
                {p.text || <span className="text-slate-400 italic">(sin texto)</span>}
              </div>
            </div>
            <div className="grid grid-cols-5 gap-1.5 pt-3 border-t border-slate-100">
              <MetricMini icon={Eye} value={fmt(p.impressions)} color="text-slate-900" />
              <MetricMini icon={Heart} value={fmt(p.reactions)} color="text-emerald-700" />
              <MetricMini icon={MessageCircle} value={fmt(p.comments)} color="text-pink-700" />
              <MetricMini icon={Share2} value={fmt(p.shares)} color="text-violet-700" />
              <MetricMini icon={Target} value={`${(p.engagement || 0).toFixed(1)}%`} color="text-amber-700" />
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function MetricMini({ icon: Icon, value, color }) {
  return (
    <div className="flex flex-col items-start gap-0.5">
      <Icon className={`h-3 w-3 ${color}`} />
      <span className={`font-mono text-[11px] tabular-nums font-semibold ${color}`}>{value}</span>
    </div>
  );
}

/* ========== Section 4: BACKLOG ========== */
function SheetSyncBanner() {
  const qc = useQueryClient();
  const [now, setNow] = useState(() => Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(t);
  }, []);
  const { data: status } = useQuery({
    queryKey: ["sheet-sync-status"],
    queryFn: fetchSheetSyncStatus,
    refetchInterval: 30 * 1000,
  });
  const mut = useMutation({
    mutationFn: triggerSheetSync,
    onSuccess: (data) => {
      const s = data.stats || {};
      toast.success(`Sincronizado · ${s.created} nuevos, ${s.updated} actualizados${s.deleted ? `, ${s.deleted} borrados` : ""}`);
      qc.invalidateQueries({ queryKey: ["sheet-sync-status"] });
      qc.invalidateQueries({ queryKey: ["client-publications"] });
      qc.invalidateQueries({ queryKey: ["client-communications"] });
      qc.invalidateQueries({ queryKey: ["clients"] });
    },
    onError: (e) => toast.error("Error al sincronizar: " + e.message),
  });

  const lastSync = status?.last_sync_at;
  const lastSyncMs = lastSync ? new Date(lastSync).getTime() : null;
  const minutesAgo = lastSyncMs ? Math.round((now - lastSyncMs) / 60000) : null;
  const timeAgo = minutesAgo === null ? "—"
    : minutesAgo < 1 ? "hace segundos"
    : minutesAgo === 1 ? "hace 1 min"
    : minutesAgo < 60 ? `hace ${minutesAgo} min`
    : `hace ${Math.round(minutesAgo / 60)} h`;
  const stats = status?.last_sync_stats || {};
  const isError = status?.last_sync_status === "error";

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="sheet-sync-banner">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${isError ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
          <RefreshCw className={`h-4 w-4 ${mut.isPending ? "animate-spin" : ""}`} />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-sm font-semibold text-slate-900">Sincronizado con Google Sheets</span>
            <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-0.5 rounded-sm border ${isError ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
              {isError ? "Error" : "OK"}
            </span>
            {status?.poll_interval_minutes && (
              <span className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-mono">
                auto cada {status.poll_interval_minutes} min
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Última sincronización <span className="font-mono">{timeAgo}</span>
            {stats.rows_processed != null && (
              <span className="text-slate-400 ml-1">· {stats.rows_processed} filas · {stats.created || 0} nuevas · {stats.updated || 0} actualizadas{stats.deleted ? ` · ${stats.deleted} borradas` : ""}</span>
            )}
            {isError && status?.last_sync_error && (
              <span className="text-rose-600 ml-1">· {status.last_sync_error}</span>
            )}
          </div>
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => mut.mutate()}
        disabled={mut.isPending}
        data-testid="sync-now-btn"
        className="bg-slate-900 hover:bg-slate-800 text-white h-9 shrink-0"
      >
        <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${mut.isPending ? "animate-spin" : ""}`} />
        {mut.isPending ? "Sincronizando…" : "Sincronizar ahora"}
      </Button>
    </div>
  );
}

function BacklogSection({ clientId, totalCount, reconciliation }) {
  const [tipoFilter, setTipoFilter] = useState("all");
  const [query, setQuery] = useState("");
  const { data: publications = [] } = useQuery({
    queryKey: ["client-publications", clientId, tipoFilter],
    queryFn: () => fetchPublications(clientId, tipoFilter === "all" ? {} : { tipo: tipoFilter }),
  });

  const filteredPubs = publications.filter((p) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (p.link || "").toLowerCase().includes(q) ||
      (p.comentarios || "").toLowerCase().includes(q) ||
      (p.mes || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4">
      <SheetSyncBanner />
      {reconciliation && reconciliation.no_metrics_count > 0 && (
        <ReconciliationBanner
          backlogTotal={reconciliation.backlog_total}
          metricoolMatched={reconciliation.metricool_matched}
          noMetricsCount={reconciliation.no_metrics_count}
          repostsCount={reconciliation.reposts_count}
          byType={reconciliation.no_metrics_by_type}
        />
      )}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-900">Listado del backlog</h2>
          <p className="text-xs text-slate-500 mt-0.5">{totalCount} piezas registradas — origen, performance, alcance, likes.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              placeholder="Buscar enlace, comentario o mes…"
              data-testid="publications-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 h-9 w-64 rounded-md bg-white border-slate-200 text-sm"
            />
          </div>
          <Select value={tipoFilter} onValueChange={setTipoFilter}>
            <SelectTrigger className="h-9 w-44 bg-white border-slate-200" data-testid="publications-type-filter">
              <Filter className="h-3.5 w-3.5 text-slate-400 mr-1.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {ALL_TYPES.map((t) => <SelectItem key={t} value={t}>{CONTENT_TYPE_META[t]?.label || t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="publications-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Origen</th>
                <th className="px-4 py-3">Mes / Sem</th>
                <th className="px-4 py-3">Pieza</th>
                <th className="px-4 py-3 text-right">Alcance</th>
                <th className="px-4 py-3 text-right">Likes</th>
                <th className="px-4 py-3 text-right">Coment.</th>
                <th className="px-4 py-3 text-right">Shares</th>
              </tr>
            </thead>
            <tbody>
              {filteredPubs.map((p) => <PublicationRow key={p.id} pub={p} />)}
              {filteredPubs.length === 0 && (
                <tr><td colSpan={9} className="px-5 py-10 text-center text-sm text-slate-500">No hay publicaciones con esos filtros.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ========== Shared components ========== */

function SectionHeader({ title, subtitle, small, icon: Icon, badge, badgeTone = "pending" }) {
  const toneClass = badgeTone === "ok" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-slate-500 bg-slate-100 border-slate-200";
  const dotClass = badgeTone === "ok" ? "bg-emerald-500" : "bg-amber-400";
  return (
    <div className={small ? "mb-3" : "mb-4 flex items-center gap-3"}>
      {Icon && !small && (
        <div className="h-9 w-9 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4" />
        </div>
      )}
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className={`font-display ${small ? "text-base" : "text-lg"} font-semibold text-slate-900`}>{title}</h2>
          {badge && (
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-semibold border px-1.5 py-0.5 rounded-sm ${toneClass}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
              {badge}
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
      </div>
    </div>
  );
}

function ReconciliationBanner({ backlogTotal, metricoolMatched, noMetricsCount, repostsCount, byType }) {
  const byTypeList = Object.entries(byType || {}).map(([t, n]) => `${n} ${CONTENT_TYPE_META[t]?.label || t}`).join(" · ");
  return (
    <div data-testid="reconciliation-banner" className="bg-sky-50/60 border border-sky-200 rounded-md p-4 flex items-start gap-3">
      <div className="h-9 w-9 rounded-md bg-sky-600 text-white flex items-center justify-center shrink-0">
        <AlertTriangle className="h-4 w-4" />
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium text-sky-900">
          {noMetricsCount} {noMetricsCount === 1 ? "pieza sin métrica individual de Metricool" : "piezas sin métrica individual de Metricool"}
        </div>
        <div className="text-xs text-sky-800/80 mt-0.5 leading-relaxed">
          Metricool no captura siempre los reposts como posts puntuales (sí los suma a métricas generales). El conteo de cumplimiento usa el <span className="font-semibold">backlog interno</span>. En el análisis, esas piezas aparecen sin números. {byTypeList && (<>Sin métricas por tipo: <span className="font-mono">{byTypeList}</span>.</>)}
        </div>
        <div className="flex items-center gap-4 mt-2 text-[10px] font-mono uppercase tracking-wider text-sky-900/80">
          <span>Backlog: <span className="text-sky-900 font-semibold">{backlogTotal}</span></span>
          <span>Con métricas: <span className="text-sky-900 font-semibold">{metricoolMatched}</span></span>
          <span>Reposts: <span className="text-sky-900 font-semibold">{repostsCount}</span></span>
        </div>
      </div>
    </div>
  );
}

function MonthlyChartTooltip({ active, payload, label, tracked }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-slate-950 text-white text-xs rounded-md p-3 shadow-md font-mono min-w-[180px]">
      <div className="text-slate-400 mb-2 uppercase tracking-wider text-[10px]">{label}</div>
      {tracked.map((t) => {
        const delivered = row[`${t}_delivered`] || 0;
        const target = row[`${t}_target`] || 0;
        const ok = delivered >= target;
        return (
          <div key={t} className="flex items-center justify-between gap-3 tabular-nums">
            <span className="text-slate-300">{CONTENT_TYPE_META[t]?.label || t}:</span>
            <span className={`font-semibold ${ok ? "text-emerald-400" : "text-sky-400"}`}>
              {delivered}/{target}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function MonthlyDeliveryChart({ data, trackedTypes }) {
  const tracked = trackedTypes && trackedTypes.length ? trackedTypes : TRACKED_TYPES;
  const allMonths = data || [];
  const [rangeMonths, setRangeMonths] = useState(allMonths.length > 6 ? 6 : allMonths.length || 0);
  const visible = rangeMonths >= allMonths.length
    ? allMonths
    : allMonths.slice(allMonths.length - rangeMonths);

  return (
    <div className="w-full" data-testid="monthly-chart">
      {allMonths.length > 6 && (
        <div className="flex items-center justify-end gap-1 mb-2 -mt-1">
          <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold mr-1">Rango</span>
          {[
            { label: "Últimos 6", v: 6 },
            { label: "Últimos 12", v: 12 },
            { label: "Plan completo", v: allMonths.length },
          ].filter((o) => o.v <= Math.max(allMonths.length, 6)).map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => setRangeMonths(o.v)}
              data-testid={`monthly-chart-range-${o.v}`}
              className={`text-[10px] font-medium px-2 py-0.5 rounded-sm border transition ${
                rangeMonths === o.v
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
      <div style={{ height: 300 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={visible} margin={{ top: 12, right: 12, left: -10, bottom: 0 }} barCategoryGap={"22%"}>
          <CartesianGrid strokeDasharray="3 6" stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
          <YAxis tick={{ fill: "#94A3B8", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} axisLine={false} width={36} />
          <Tooltip cursor={{ fill: "#F1F5F9" }} content={<MonthlyChartTooltip tracked={tracked} />} />
          <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Sans", color: "#64748B", paddingTop: 8 }} iconType="square" iconSize={8} />
          {tracked.map((t) => (
            <Bar key={t} dataKey={`${t}_delivered`} name={CONTENT_TYPE_META[t]?.label || t} fill={CONTENT_TYPE_META[t]?.color || "#64748B"} radius={[3, 3, 0, 0]} isAnimationActive={false} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
}

function StreamCard({ stream, testId }) {
  const meta = CONTENT_TYPE_META[stream.type] || { label: stream.type, color: "#64748B" };
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300 transition-colors">
      <div className="flex items-center gap-2 mb-3">
        <span className="h-2 w-2 rounded-sm" style={{ background: meta.color }} />
        <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{meta.label}</span>
      </div>
      <div className="font-mono text-2xl font-semibold text-slate-900 tabular-nums">{stream.count}</div>
      <div className="text-[10px] text-slate-400 mt-0.5">publicaciones con datos</div>
      <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2 text-xs">
        <SmallStat icon={Eye} value={stream.reach} label="Alcance" />
        <SmallStat icon={Heart} value={stream.likes} label="Likes" />
        <SmallStat icon={MessageCircle} value={stream.comments} label="Coment." />
        <SmallStat icon={Share2} value={stream.shares} label="Shares" />
      </div>
    </div>
  );
}

function SmallStat({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-1.5 text-slate-600">
      <Icon className="h-3 w-3 text-slate-400 shrink-0" />
      <div className="leading-tight min-w-0">
        <div className="font-mono text-xs font-medium text-slate-900 tabular-nums truncate">{formatNumber(value)}</div>
        <div className="text-[10px] text-slate-400">{label}</div>
      </div>
    </div>
  );
}

function sanitizeShareLink(url) {
  if (!url) return url;
  try {
    const u = new URL(url);
    // LinkedIn blocks posts when share-tracking params are present (utm_*, rcm,
    // trk, lipi, …). Stripping all params yields the canonical post URL which
    // opens normally. Other hosts are left untouched in case they need the
    // querystring (e.g. UTM-based analytics on a blog).
    if (/(^|\.)linkedin\.com$/i.test(u.hostname)) {
      u.search = "";
      u.hash = "";
      return u.toString();
    }
    return url;
  } catch {
    return url;
  }
}

function openExternal(e, url) {
  if (!url) return;
  e.preventDefault();
  e.stopPropagation();
  window.open(sanitizeShareLink(url), "_blank", "noopener,noreferrer");
}

function TopPerformerCard({ pub, testId }) {
  const meta = CONTENT_TYPE_META[pub.tipo] || { label: pub.tipo, color: "#64748B" };
  const dateStr = pub.fecha ? new Date(pub.fecha).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  return (
    <div
      data-testid={testId}
      onClick={(e) => openExternal(e, pub.link)}
      className="bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300 hover:shadow-sm transition-all flex flex-col gap-3 cursor-pointer select-none"
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-[0.14em] font-semibold text-white" style={{ background: meta.color }}>
          {meta.label}
        </span>
        <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
      </div>
      <div className="text-sm text-slate-700 line-clamp-2 leading-snug">
        {pub.comentarios || pub.link?.split("?")[0]?.split("/").slice(-1)[0]?.replace(/-/g, " ") || "Publicación"}
      </div>
      <div className="mt-auto grid grid-cols-4 gap-2 pt-3 border-t border-slate-100">
        <MiniStat icon={Eye} value={pub.reach} />
        <MiniStat icon={Heart} value={pub.likes} />
        <MiniStat icon={MessageCircle} value={pub.comments} />
        <MiniStat icon={Share2} value={pub.shares} />
      </div>
      <div className="text-[10px] text-slate-400 font-mono">{dateStr}</div>
    </div>
  );
}

function MiniStat({ icon: Icon, value }) {
  return (
    <div className="flex items-center gap-1 text-slate-500">
      <Icon className="h-3 w-3" />
      <span className="font-mono text-xs tabular-nums">{formatNumber(value || 0)}</span>
    </div>
  );
}

function SourceBadge({ pub }) {
  const source = pub.source || "backlog";
  if (pub.is_repost) {
    return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm"><Repeat2 className="h-2.5 w-2.5" /> Repost</span>;
  }
  if (source === "both") {
    return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm"><CheckCircle2 className="h-2.5 w-2.5" /> Match</span>;
  }
  if (source === "metricool") {
    return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-sky-700 bg-sky-50 border border-sky-200 px-1.5 py-0.5 rounded-sm"><span className="h-1.5 w-1.5 rounded-full bg-sky-500" /> Metricool</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-600 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-sm"><span className="h-1.5 w-1.5 rounded-full bg-slate-400" /> Backlog</span>;
}

function PublicationRow({ pub }) {
  const meta = CONTENT_TYPE_META[pub.tipo] || { label: pub.tipo, color: "#64748B" };
  const dateStr = pub.fecha ? new Date(pub.fecha).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—";
  const linkLabel = pub.link
    ? (pub.link.split("?")[0].split("/").slice(-1)[0] || pub.link).slice(0, 56).replace(/-/g, " ")
    : "(sin enlace)";
  const noMetrics = !pub.has_metrics;
  return (
    <tr className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 ${pub.is_repost ? "bg-amber-50/30" : ""}`}>
      <td className="px-4 py-2.5 font-mono text-xs text-slate-600 whitespace-nowrap">{dateStr}</td>
      <td className="px-4 py-2.5">
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-semibold text-white" style={{ background: meta.color }}>
          {meta.label}
        </span>
      </td>
      <td className="px-4 py-2.5"><SourceBadge pub={pub} /></td>
      <td className="px-4 py-2.5 text-xs text-slate-600 font-mono whitespace-nowrap">
        {pub.mes ? `${pub.mes.charAt(0).toUpperCase() + pub.mes.slice(1).toLowerCase()}` : "—"}
        {pub.semana ? ` · S${pub.semana}` : ""}
      </td>
      <td className="px-4 py-2.5 max-w-[340px]">
        {pub.link ? (
          <button
            type="button"
            onClick={(e) => openExternal(e, pub.link)}
            data-testid={`pub-link-${pub.id}`}
            className="text-left text-sm text-slate-900 hover:underline hover:text-sky-700 inline-flex items-center gap-1.5 max-w-full"
          >
            <span className="truncate max-w-[260px]">{linkLabel}</span>
            <ExternalLink className="h-3 w-3 text-slate-400 shrink-0" />
          </button>
        ) : (
          <span className="text-sm text-slate-500">{pub.comentarios || "—"}</span>
        )}
        {pub.comentarios && pub.link && (
          <div className="text-[10px] text-slate-400 truncate max-w-[300px]">{pub.comentarios}</div>
        )}
      </td>
      <td className="px-4 py-2.5 text-right font-mono text-xs tabular-nums text-slate-700">
        {noMetrics ? <span className="text-slate-300">—</span> : formatNumber(pub.reach || 0)}
      </td>
      <td className="px-4 py-2.5 text-right font-mono text-xs tabular-nums text-slate-700">
        {noMetrics ? <span className="text-slate-300">—</span> : formatNumber(pub.likes || 0)}
      </td>
      <td className="px-4 py-2.5 text-right font-mono text-xs tabular-nums text-slate-700">
        {noMetrics ? <span className="text-slate-300">—</span> : formatNumber(pub.comments || 0)}
      </td>
      <td className="px-4 py-2.5 text-right font-mono text-xs tabular-nums text-slate-700">
        {noMetrics ? <span className="text-slate-300">—</span> : formatNumber(pub.shares || 0)}
      </td>
    </tr>
  );
}
